import {ShapeLineStyle, ShapeWidgetData, Widget} from '@dv5/domain'
import React, {FC, useMemo} from 'react'
import {ColorPicker} from '../common/ColorPicker'
import {SHAPE_COLORS} from '../shape/constants'
import {WidgetDataChange, canvasWidgetsDataService} from '../../services/canvasWidgetsDataService'
import {ShapeLineStylePicker} from '../shape/ShapeLineStylePicker'
import {getShapeLineStyle} from '../shape/shapeLineStyle'

interface Props {
    canvasId: string
    widgets: Widget[]
}

export const ShapeWidgetPropertiesPanel: FC<Props> = ({
    canvasId,
    widgets,
}) => {
    const dataOf = (w: Widget) => w.data as ShapeWidgetData

    const bgColorMixed = useMemo(() => new Set(widgets.map(w => dataOf(w).bgColor)).size > 1, [widgets])
    const borderColorMixed = useMemo(
        () => new Set(widgets.map(w => dataOf(w).borderColor)).size > 1,
        [widgets],
    )
    const borderWidthMixed = useMemo(
        () => new Set(widgets.map(w => dataOf(w).borderWidth ?? 1)).size > 1,
        [widgets],
    )

    const lineStyleMixed = useMemo(
        () => new Set(widgets.map(w => getShapeLineStyle(dataOf(w).lineStyle))).size > 1,
        [widgets],
    )

    const first = widgets.length > 0 ? dataOf(widgets[0]) : null

    const bgColor = !bgColorMixed && first ? first.bgColor : ''
    const borderColor = !borderColorMixed && first ? first.borderColor : ''
    const borderWidth = !borderWidthMixed && first ? String(first.borderWidth ?? 1) : '–'

    const allAtMinBorderWidth = widgets.every(w => (dataOf(w).borderWidth ?? 1) <= 0)
    const allAtMaxBorderWidth = widgets.every(w => (dataOf(w).borderWidth ?? 1) >= 12)

    const applyPatch = (
        patchedPaths: Array<string>,
        makeData: (data: ShapeWidgetData) => ShapeWidgetData,
    ) => {
        const changes: Array<WidgetDataChange> = widgets.map(widget => ({
            widgetId: widget.id,
            patchedPaths,
            data: makeData(dataOf(widget)),
        }))
        canvasWidgetsDataService.changeWidgetsData(canvasId, changes)
    }

    const handleBgColorChange = (color: string) => {
        applyPatch(['bgColor'], data => ({...data, bgColor: color}))
    }

    const handleBorderColorChange = (color: string) => {
        applyPatch(['borderColor'], data => ({...data, borderColor: color}))
    }

    const handleLineStyleChange = (lineStyle: ShapeLineStyle) => {
        applyPatch(['lineStyle'], data => ({...data, lineStyle}))
    }

    const handleBorderWidthChange = (delta: number) => {
        applyPatch(['borderWidth'], data => ({
            ...data,
            borderWidth: Math.max(0, Math.min(12, (data.borderWidth ?? 1) + delta)),
        }))
    }

    return (
        <div
            className='align-widgets-bar__properties'
            onMouseDown={e => e.stopPropagation()}
        >
            <ColorPicker
                value={bgColor}
                presets={SHAPE_COLORS}
                onChange={handleBgColorChange}
                mixed={bgColorMixed}
            />
            <ColorPicker
                value={borderColor}
                presets={SHAPE_COLORS}
                onChange={handleBorderColorChange}
                mixed={borderColorMixed}
            />
            <ShapeLineStylePicker
                value={first?.lineStyle}
                mixed={lineStyleMixed}
                onChange={handleLineStyleChange}
            />
            <div className='align-widgets-bar__border-width'>
                <button
                    className='align-widgets-bar__button'
                    onClick={() => handleBorderWidthChange(-1)}
                    aria-label='Decrease border width'
                    type='button'
                    disabled={allAtMinBorderWidth}
                >
                    −
                </button>
                <span className='align-widgets-bar__border-value'>{borderWidth}</span>
                <button
                    className='align-widgets-bar__button'
                    onClick={() => handleBorderWidthChange(1)}
                    aria-label='Increase border width'
                    type='button'
                    disabled={allAtMaxBorderWidth}
                >
                    +
                </button>
            </div>
        </div>
    )
}
